import { useState, useEffect, useMemo } from "react";
import useDevice from "../useDevice";
import { isSafari } from "../../util/util";
import { useLocalStorage, useSessionStorage } from "@uidotdev/usehooks";

const VIDEO_SIZE = {
	'640 X 480': { width: 640, height: 480 },
	'640 X 360': { width: 640, height: 360 },
	'360 X 270': { width: 360, height: 270 }
};

export function getVideoConfig(isDesktop) {
	return {
		audio: false, 
		video: {
			facingMode: 'user',
			width: 720, 
			height: 560,
			frameRate: {
				ideal: 60,
			},
		}
	}
}

const defaultCameras = [];


export default function useCamera({ isDetect = false }) {

	const whatdevice = useDevice();
	const isDesktop = whatdevice === "desktop";

	const VIDEO_CONFIG = useMemo(() => {
		return {
			...getVideoConfig(isDesktop)
		}
	}, [isDesktop])

	const [defaultCamera, setDefaultCamera] = useState(null);

	const [hasCameraPermission, setHasCameraPermission] = useState(false);

	const [currentInputDevice, setCurrentInputDevice] = useLocalStorage(
		"cameras",
		null
	);

	const [ cameraError, setCameraError ] = useState("");

	const [videoInputDevices, setVideoInputDevices] = useState([]);

	const [isCameraDenied, setIsCameraDenied] = useState(null);

	const [permissionChange, triggerPermissionChange] = useState("");

	const [ detected, setDetected ] = useState(isDetect);


	const currentPermission = useMemo(() => {

		if (!isSafari() && ["denied", "granted", "prompt"].includes(permissionChange)) {

			return permissionChange;

		}

		if (isCameraDenied) return "denied";

		if (!isCameraDenied) return "granted";


		return "prompt"


	}, [isCameraDenied, hasCameraPermission, permissionChange]);



	const getCameras = async () => {
		/*
		 * Additional refreshing for permissions on Firefox due to assumption of lack of permissions bug
		 */
		if (navigator.userAgent.indexOf("Firefox") !== -1) {
			await navigator.mediaDevices.getUserMedia(VIDEO_CONFIG);
		}

		const devices = await navigator.mediaDevices.enumerateDevices();
		let videoInputDevices = devices.filter(
			(device) => device.kind === "videoinput"
		);

		const groupIds = videoInputDevices
			.map((device) => device.groupId)
			.map((id, index, arr) => arr.indexOf(id) === index);

		videoInputDevices = videoInputDevices.filter(
			(item, index) => groupIds[index]
		);

		return videoInputDevices;
	};

	const getCameraPermission = async () => {

		let permission = false;

		setCameraError("");

		try {
			// console.log(VIDEO_CONFIG);
			const stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONFIG);
			permission = true;
			stream.getTracks().forEach((track) => track.stop());
		} catch (error) {
			switch(error.name) {

				case "NotAllowedError":
					break;
				case "NotFoundError":
					setCameraError("No video source or cameras could be accessed");
					break;
				case "NotReadableError":
					setCameraError("Your video source is not readable. This means some other device/app is likely using it.");
					break;
				case "OverconstrainedError":
				case "SecurityError":
				case "AbortError":
					setCameraError("Your camera is not supported.");
					break;
			}
			console.error(error.message,error.name);
			permission = false;
		}

		setIsCameraDenied(!permission);
		setHasCameraPermission(permission);

		return permission ? "granted" : "denied";
	}

	const getDefaultCamera = async (devices = []) => {
		if ((await getCameraPermission()) === "denied") return null;

		if (devices.length === 0) {
			devices = await getCameras();
		}

		if (devices.length && !isDesktop && devices[0].deviceId) {
			return devices[0];
		}

		for (let i = 0; i < devices.length; i++) {
			const device = devices[0];
			if (defaultCameras.includes(device.deviceId)) {
				return device;
			}
		}

		return devices[0].deviceId ? devices[0] : null;
	}

	const search = async (internal = false) => {

		// So someone from the frontend did this
		
		if(!internal) {
			setDetected(true);
		}
		
		if(internal && !detected) return false;

		if ((await getCameraPermission()) === "granted") {
			// We will get all audio devices
			const devices = await getCameras();
			const defaultCamera = await getDefaultCamera(devices);

			let preferredDevice = currentInputDevice;

			if(!preferredDevice) {
				preferredDevice = defaultCamera;
			}

			preferredDevice = [...devices].filter(item => {
				return item?.label?.toLowerCase() === (preferredDevice?.label ?? localCamera?.label)?.toLowerCase()
			})?.[0];


			if("ondevicechange" in navigator.mediaDevices) {

				navigator.mediaDevices.addEventListener("devicechange", async () => {

					if(await getCameraPermission() === "granted") {
						const localCamera = defaultCamera;
						const devices = await getCameras();
						const defaultCamera = await getDefaultCamera(devices);
						let preferredDevice = currentInputDevice;

						// We need to update the preferredDevice;
						
						if(!preferredDevice) {
							preferredDevice = defaultCamera;
						} else {
							preferredDevice = [...devices].filter(item => {
								return item?.label?.toLowerCase() === (preferredDevice?.label ?? localCamera?.label)?.toLowerCase()
							})?.[0];	
						}
						
						// So I will just switch to the default iff I already like my default
						if(!preferredDevice || preferredDevice?.groupId === localMicrophone?.groupId) {
							preferredDevice = defaultCamera;
						}
						
						setVideoInputDevices(devices);
						setCurrentInputDevice(preferredDevice);
						setDefaultCamera(defaultCamera);

					} else {

						setVideoInputDevices([]);
						setDefaultCamera(null);
						setHasCameraPermission(false);
						setIsCameraDenied(true);
					}


				})
			}


			setVideoInputDevices(devices);
			setCurrentInputDevice(preferredDevice);
			setDefaultCamera(defaultCamera);
		
			return true;

		} else {

			setVideoInputDevices([]);
			setDefaultCamera(null);
			setHasCameraPermission(false);
			setIsCameraDenied(true);

		}

		return false;
	};

	useEffect( () => {
		
		if(!detected) return () => false;

		if(detected) search(true);

		return () => true;

		
	}, [permissionChange]);

	useEffect( () => {

		if(!isSafari()) {
			// Apple is apple :-)
			try {
				navigator.permissions.query({
					name: "camera"
				}).then( (permissionState) => {

					triggerPermissionChange(permissionState.state)
	
					permissionState.onchange = () => {
						// console.log(permissionState.state)
						
						triggerPermissionChange(permissionState.state);
					}
	
				});
			} catch (_err) {
				// Ignore
			}
		}

	}, []);

	return {
		search,
		hasCameraPermission,
		isCameraDenied,
		videoInputDevices,
		preferredInputDevice: currentInputDevice,
		acquireInputDevice: (device) => setCurrentInputDevice(device),
		defaultCamera,
		currentPermission,
		getCameras,
		getDefaultCamera,
		getCameraPermission,
		cameraError,
	}
}
